import { initialLanguageOption } from 'constants/languages';
import { Formik, useField } from 'formik';
import { SupportedLocale as SupportedLocaleType } from 'generated/graphql';
import { FC, useMemo } from 'react';
import * as yup from 'yup';
import {
  getSupportedLocaleMetadata,
  SupportedLocale,
} from '@sz/l10n';
import {
  FormikCheckboxFormField,
  FormikMultiselectFormField,
  FormikSelectFormField,
  FormikTextInputField,
  InlineNotification,
  ModalButtons,
  NotificationType,
  TMultiselectOption,
  TSelectOption,
} from '@spotted-zebra-uk/ui-components';

interface IInviteNewCandidateForm {
  availableAssignees: TMultiselectOption[];
  projectLanguages: SupportedLocaleType[] | undefined;
  projectId: number;
  onCancel: () => void;
  onSubmit: (
    givenName: string,
    familyName: string,
    markedAsInternalCandidate: boolean,
    email: string,
    managerEmail: string,
    assignees: TMultiselectOption[],
    locale: SupportedLocaleType
  ) => void;
}

interface IInviteNewCandidateFormValues {
  givenName: string;
  familyName: string;
  email: string;
  markedAsInternalCandidate: boolean;
  managerFullName: string;
  managerEmail: string;
  assignees: TMultiselectOption[];
  language: TSelectOption<SupportedLocaleType>;
}

const initialValues: IInviteNewCandidateFormValues = {
  givenName: '',
  familyName: '',
  email: '',
  markedAsInternalCandidate: false,
  assignees: [],
  language: initialLanguageOption,
  managerFullName: '',
  managerEmail: '',
};

const validationSchema = yup.object().shape({
  givenName: yup
    .string()
    .required('This is a required field.')
    .max(50, 'Must be under 50 characters.'),
  familyName: yup
    .string()
    .required('This is a required field.')
    .max(50, 'Must be under 50 characters.'),
  email: yup.string().required().email(),
  language: yup
    .object()
    .shape({ label: yup.string(), value: yup.string().required() }),
});

const InviteNewCandidateForm: FC<IInviteNewCandidateForm> = props => {
  const handleFromSubmit = (values: IInviteNewCandidateFormValues) => {
    props.onSubmit(
      values.givenName,
      values.familyName,
      values.markedAsInternalCandidate,
      values.email,
      values.managerEmail,
      values.assignees,
      values.language.value || SupportedLocaleType.English
    );
  };

  const languageSelectFieldOptions = useMemo(
    () =>
      props.projectLanguages?.length
        ? props.projectLanguages.map(locale => ({
            label:
              getSupportedLocaleMetadata(SupportedLocale[locale])?.name
                ?.english || locale,
            value: locale,
          }))
        : [initialLanguageOption],
    [props.projectLanguages]
  );

  return (
    <Formik<IInviteNewCandidateFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleFromSubmit}
    >
      {({ handleSubmit, values }) => (
        <div className="invite-new-candidate-form">
          <div className="invite-new-candidate-form__fields">
            <FormikTextInputField
              id="givenName"
              name="givenName"
              label="Given Name"
              placeholder="Given Name"
              data-testid="invite-candidate__given-name"
              useFormikField={useField}
            />
            <FormikTextInputField
              id="familyName"
              name="familyName"
              label="Family Name"
              placeholder="Family Name"
              data-testid="invite-candidate__family-name"
              useFormikField={useField}
            />
            <FormikTextInputField
              id="email"
              name="email"
              label="Email"
              placeholder="Email"
              useFormikField={useField}
            />
            <FormikCheckboxFormField
              id="markedAsInternalCandidate"
              name="markedAsInternalCandidate"
              label="Mark as internal candidate"
              useFormikField={useField}
            />
            {values.markedAsInternalCandidate && (
              <InlineNotification
                title="Make candidate log in with SSO."
                notificationType={NotificationType.WARNING}
                className="invite-new-candidate-form__sso-notification"
              >
                <p>
                  Only use this for candidates from your organization who are
                  logging in with company email address.
                </p>
              </InlineNotification>
            )}
            {
              <FormikMultiselectFormField
                id="assignees"
                name="assignees"
                label="Assignees"
                placeholder="Assignees"
                options={props.availableAssignees}
                useFormikField={useField}
              />
            }
            <FormikSelectFormField
              id="language"
              name="language"
              label="Language"
              placeholder="Language"
              options={languageSelectFieldOptions}
              isDisabled={!props.projectLanguages?.length}
              data-testid="direct-invite-candidate__language-select-form"
              useFormikField={useField}
              hasClearIndicator={false}
            />
          </div>
          <ModalButtons
            onConfirmButtonClick={() => handleSubmit()}
            onCancelButtonClick={props.onCancel}
            confirmButtonChildren="Invite"
            confirmButtonProps={{
              size: 'medium',
            }}
            cancelButtonProps={{
              size: 'medium',
            }}
          />
        </div>
      )}
    </Formik>
  );
};

export default InviteNewCandidateForm;
